import { useEffect, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { SlideOver } from '../common/SlideOver';
import api from '../../lib/api';
import type { OnsiteFlagRow } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  period?: { from: string; to: string };
  teamId?: string | null;
  repId?: string;       // when set, filter to a single rep
  repName?: string;
}

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function OnsiteFlagsPanel({ open, onClose, period, teamId, repId, repName }: Props) {
  const [flags, setFlags] = useState<OnsiteFlagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !period) return;
    let cancelled = false;
    setLoading(true); setError(null);
    const params: Record<string, string> = { scope: 'team', from: period.from, to: period.to };
    if (teamId) params.teamId = teamId;
    api.get('/api/dashboard/cockpit/onsite-flags', { params })
      .then((res) => { if (!cancelled) setFlags(res.data.flags ?? []); })
      .catch(() => { if (!cancelled) setError('Could not load flagged visits.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, period?.from, period?.to, teamId]);

  const visible = useMemo(
    () => (repId ? flags.filter((f) => f.repId === repId) : flags),
    [flags, repId],
  );

  const title = repName ? `Needs review · ${repName}` : 'On-campus visits needing review';

  return (
    <SlideOver isOpen={open} onClose={onClose} title={title}>
      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
      {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No flagged visits in this period.</p>
      )}
      <ul className="space-y-3">
        {visible.map((f) => (
          <li key={f.activityId} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">{f.orgName ?? 'Unknown school'}</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
                <MapPin className="h-3.5 w-3.5" />
                {f.distanceMiles != null ? `${f.distanceMiles.toFixed(1)} mi away` : '—'}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span>{f.personName}</span>
              {' · '}
              <span>{f.repName ?? 'Unknown rep'}</span>
              {' · '}
              <span>{dateLabel(f.activityAt)}</span>
              {f.locationAccuracyM != null && <>{' · '}<span>±{Math.round(f.locationAccuracyM)}m</span></>}
            </div>
          </li>
        ))}
      </ul>
    </SlideOver>
  );
}
